import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { loginStoreActions, RootStoreState } from '@app/root-store';
import { LoginStoreEffects } from '@app/root-store/login-store/effects';
import { Dimensions, ImageCroppedEvent, ImageTransform } from '@app/shared/packages/image-cropper/interfaces';
import { FileUploadService } from '@app/shared/services/file-upload/file-upload.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { ProfileStepperBarComponent } from '../profile-stepper-bar/profile-stepper-bar.component';
import { ToastrService } from 'ngx-toastr';
import heic2any from 'heic2any';

import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'bs-file-upload',
  templateUrl: './bs-file-upload.component.html',
})
export class BsFileUploadComponent implements OnInit, OnDestroy {
  imageChangedEvent: any = '';
  croppedImage: any;
  canvasRotation = 0;
  rotation = 0;
  scale = 1;
  showCropper = false;
  format = null;
  showdiv = false;
  containWithinAspectRatio = false;
  videoMimeType: any;
  resizeToWidth = 465;
  cropperMinWidth = 250;
  screenWidth: number;
  transform: ImageTransform = {};
  @ViewChild('videoPlayer') videoplayer: ElementRef;

  // login state object
  loginStateObject;
  imageUploadedSuccess = false;
  showLoader = false;
  subscription = new Subscription();

  constructor(
    private fileUploadService: FileUploadService,
    public activeModal: NgbActiveModal,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
    private loginService: LoginStoreEffects,
    private store$: Store<RootStoreState.State>,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<BsFileUploadComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.subscription.add(
      this.loginService.loginObject.subscribe((state) => {
        this.loginStateObject = state.data;
      })
    );

    if (this.loginStateObject.profile_image_url) {
      this.imageUploadedSuccess = true;
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnInit() {
    this.screenWidth = window.innerWidth;
    if (this.data.type == '') {
      this.resizeToWidth = 150;
      this.cropperMinWidth = 250;
    }

    if (this.loginStateObject.profile_image_url) {
      this.imageUploadedSuccess = true;
    }
  }

  addContactInformation(editType) {
    this.dialog.closeAll();
    const modalRef = this.dialog.open(ProfileStepperBarComponent, {
      data: { editType: editType, triggeredFrom: 'continueBtn' },
      autoFocus: false,
      panelClass: 'full-window-dialog-2',
    });
    this.subscription.add(
      modalRef.afterClosed().subscribe((data) => {
        if (data) {
          this.store$.dispatch(new loginStoreActions.LoginRequestAction());
        }
      })
    );
  }

  async fileChangeEvent(event: any): Promise<void> {
    const file = event.target.files && event.target.files[0];

    if (!file) return;

    const isHeic = file.name.toLowerCase().endsWith('.heic');

    if (isHeic) {
      const jpgFile = await this.convertHeicToJpg(file);
    }

    if (file.type.indexOf('image') > -1) {
      this.format = 'image';
      this.showdiv = true;
      this.imageChangedEvent = event;
      this.cd.markForCheck();
    } else if (file.type.indexOf('video') > -1) {
      this.toastr.error('Please upload a JPEG or PNG less than 10MB');
    }
  }
  async convertHeicToJpg(heicFile: File) {
    try {
      const arrayBuffer = await heicFile.arrayBuffer();
      const blob = new Blob([arrayBuffer]);

      const convertedBlob = await heic2any({
        blob,
        toType: 'image/jpeg',
        quality: 0.8,
      });

      // Sometimes heic2any returns a PNG if it detects browser compatibility
      const finalBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

      const jpgFile = new File([finalBlob], heicFile.name.replace(/\.heic$/i, '.jpg'), {
        type: 'image/jpeg',
        lastModified: heicFile.lastModified,
      });

      const fakeEvent = {
        target: {
          files: [jpgFile],
        },
      };

      this.fileChangeEvent(fakeEvent); // Reuse logic
    } catch (error: any) {
      if (error?.message?.includes('already browser readable')) {
        console.warn('Skipped conversion: already readable');
        // Fallback: use the original file as-is and treat it like a normal image
        const fallbackFile = new File([heicFile], heicFile.name.replace(/\.heic$/i, '.jpg'), {
          type: 'image/jpeg',
          lastModified: heicFile.lastModified,
        });

        const fakeEvent = {
          target: {
            files: [fallbackFile],
          },
        };

        this.fileChangeEvent(fakeEvent);
      } else {
        console.error('HEIC conversion failed:', error);
      }
    }
  }

  toggleVideo(event: any) {
    this.videoplayer.nativeElement.play();
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }

  imageLoaded() {
    this.showCropper = true;
  }

  cropperReady(sourceImageDimensions: Dimensions) {}

  loadImageFailed() {}

  rotateLeft() {
    this.canvasRotation--;
    this.flipAfterRotate();
  }

  rotateRight() {
    this.canvasRotation++;
    this.flipAfterRotate();
  }

  private flipAfterRotate() {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH,
    };
  }

  flipHorizontal() {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH,
    };
  }

  flipVertical() {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV,
    };
  }

  resetImage() {
    this.scale = 1;
    this.rotation = 0;
    this.canvasRotation = 0;
    this.transform = {};
  }

  zoomOut() {
    this.scale -= 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  zoomIn() {
    this.scale += 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  toggleContainWithinAspectRatio() {
    this.containWithinAspectRatio = !this.containWithinAspectRatio;
  }

  updateRotation() {
    this.transform = {
      ...this.transform,
      rotate: this.rotation,
    };
  }
  uploadFile(file) {
    let fd = {
      file: this.croppedImage,
      type: this.format,
    };
    switch (this.data.type) {
      case 'profile':
        this.profilepic(fd);
        // this.store$.dispatch(new loginStoreActions.LoginRequestAction());
        // this.cd.detectChanges;
        break;
      case 'post-upload':
        this.postpic(fd);
        this.store$.dispatch(new loginStoreActions.LoginRequestAction());
        this.dialogRef.close({
          confirmation: true,
        });
        break;
      case 'message-upload':
        this.messagepic(fd);
        this.store$.dispatch(new loginStoreActions.LoginRequestAction());
        this.dialogRef.close({
          confirmation: true,
        });
        break;
      default:
        this.store$.dispatch(new loginStoreActions.LoginRequestAction());
        this.dialogRef.close({
          confirmation: true,
        });
        break;
    }
  }

  messagepic(input) {
    this.subscription.add(
      this.fileUploadService.messagePic(input).subscribe(
        (data: any) => {
          this.activeModal.close(data);
        },
        (error: any) => {
          this.activeModal.close(error);
        }
      )
    );
  }
  postpic(input) {
    this.subscription.add(
      this.fileUploadService.postPic(input).subscribe(
        (data: any) => {
          this.activeModal.close(data);
        },
        (error: any) => {
          this.activeModal.close(error);
        }
      )
    );
  }
  async profilepic(input) {
    this.subscription.add(
      this.fileUploadService.profilePic(input).subscribe(
        (data: any) => {
          this.showLoader = false;
          this.toastr.success('Profile picture has been updated successfully');
          this.store$.dispatch(new loginStoreActions.LoginRequestAction());
          if (this.data.triggeredFrom != 'continueBtn') {
            this.dialogRef.close({
              confirmation: true,
            });
          } else {
            this.addContactInformation('contactInfo');
          }
          this.cd.detectChanges;
        },
        (error: any) => {
          this.activeModal.close(error);
        }
      )
    );
  }

  DataURIToBlob(dataURI: string) {
    const splitDataURI = dataURI.split(',');
    const byteString = splitDataURI[0].indexOf('base64') >= 0 ? atob(splitDataURI[1]) : decodeURI(splitDataURI[1]);
    const mimeString = splitDataURI[0].split(':')[1].split(';')[0];

    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);

    return new Blob([ia], { type: mimeString });
  }

  uploadFiles() {
    this.showLoader = true;
    const fileUpload = document.getElementById('fileUpload') as HTMLInputElement;
    fileUpload.value = '';
    this.uploadFile(fileUpload);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
